import React, { useEffect } from 'react';
import ReportAolFilterPanel from '../report-aol-filter-panel/ReportAolFilterPanel';
import { ClearFilterIcon, FromToWrapper, SortButtonWrapper, TextContent, Wrapper } from './AolFilterHeader.style';
import { useVesselStore } from '../../../context/VesselContext';
import { useAolStore } from '../../../context/AolContext';
import { PageType } from '../../../types/Page';
import useGetAolData from '../../../api/aol/useGetAolData';
import { useAuthStore } from '../../../context/AuthContext';
import { RoleTypeEnum } from '../../../enums/roleTypeEnum';
import { Period } from '../../../enums/PeriodEnum';
import { SortButton } from '../../../styles/button.style';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useUserSettingsStore } from '../../../context/UserSettingsContext';
import useUserSettingsApi from '../../../api/useUserSettingsApi';
import { formatAolUrlDate } from '../../../utils/aol/aolDateUtils';
import useCargoTempLogApi from '../../../api/cargo-temp-log/useCargoTempLogApi';
import { prettifyDate } from 'odfjell-date-time-utils/dateTimeFormatter';
import { subtractDateDays, getDateNow } from 'odfjell-date-time-utils/dateTimeUtils';
import { AppFeatureEnum } from 'src/enums/appFeatureEnum';
import useAppFeature from 'src/app/useAppFeature';

type Props = {
    page: PageType;
    onChange: (fromDate: Date, toDate: Date) => void;
};

const AolFilterHeaderComponent: React.FC<Props> = (props) => {
    const { selectedVessel } = useVesselStore();
    const { aolFilter, setAolFilter, setPeriod, aolListSortAsc, setAolListSortAsc, setDefaultAolFilter } = useAolStore();
    const { role } = useAuthStore();
    const { userSettings, userSettingsLoaded } = useUserSettingsStore();
    const { upsertUserSettings } = useUserSettingsApi();
    const { getAolData } = useGetAolData();
    const { getCargoTempLogDates } = useCargoTempLogApi();
    const { getAppFeatureFromDateForVessel } = useAppFeature();

    const setDefaultPeriod = () => {
        if (userSettings?.aolPeriod) {
            setPeriod(Period[userSettings.aolPeriod]);
            setDefaultAolFilter({
                fromDate: subtractDateDays(getDateNow(), userSettings.aolPeriod === Period.TwoWeeks ? 14 : 28),
                toDate: new Date(),
                isDefault: true,
            });
        } else if (role?.roleType === RoleTypeEnum.ShipUser) setPeriod(Period.TwoWeeks);
        else setPeriod(Period.FourWeeks);
    };

    useEffect(() => {
        if (userSettingsLoaded && !userSettings?.aolPeriod) setDefaultPeriod();
    }, [role, userSettingsLoaded]);

    const getDefaultAolFromDate = () => {
        if (userSettings?.aolPeriod) return subtractDateDays(getDateNow(), Period[userSettings.aolPeriod] === Period.TwoWeeks ? 14 : 28);
        else if (role?.roleType === RoleTypeEnum.ShipUser) return subtractDateDays(getDateNow(), 14);
        return subtractDateDays(getDateNow(), 28);
    };

    const clearDateFilter = () => {
        const aolFromDateTemp = getDefaultAolFromDate();
        const aolToDateTemp = getDateNow();

        sessionStorage.setItem('userChangedFilter', 'true');
        setAolFilter({ fromDate: aolFromDateTemp, toDate: aolToDateTemp, isDefault: true });
        if (props.page !== 'cargoTemp') {
            getAolData(selectedVessel!.imoNumber, formatAolUrlDate(aolFromDateTemp), formatAolUrlDate(aolToDateTemp), true);
        } else if (props.page === 'cargoTemp') {
            const stowageIntegrationFromDate = getAppFeatureFromDateForVessel({
                featureEnum: AppFeatureEnum.StowageIntegration,
                imoNumber: selectedVessel.imoNumber,
            });
            getCargoTempLogDates(
                selectedVessel!.imoNumber,
                formatAolUrlDate(aolFromDateTemp),
                formatAolUrlDate(aolToDateTemp),
                formatAolUrlDate(stowageIntegrationFromDate),
                false
            );
        }
    };

    const changeSort = () => {
        setAolListSortAsc(!aolListSortAsc);
        upsertUserSettings({ ...userSettings, sortAsc: !aolListSortAsc });
    };

    return (
        <Wrapper className={'aol-list-header'}>
            <ReportAolFilterPanel onChange={props.onChange} page={props.page} />
            <TextContent>
                {aolFilter?.fromDate && aolFilter?.toDate && (
                    <FromToWrapper>
                        <span className="info-text text-overflow">
                            From {prettifyDate(aolFilter.fromDate)} to {prettifyDate(aolFilter.toDate)}
                        </span>
                        {!aolFilter.isDefault && (
                            <ClearFilterIcon onClick={clearDateFilter} title="Click to reset filter">
                                <i className="fa-solid fa-circle-xmark"></i>
                            </ClearFilterIcon>
                        )}
                    </FromToWrapper>
                )}
            </TextContent>
            <SortButtonWrapper>
                <SortButton isSelected={false} onClick={() => changeSort()}>
                    <FontAwesomeIcon title="Click to change sorting" icon={aolListSortAsc ? 'sort-amount-up' : 'sort-amount-down-alt'} />
                </SortButton>
            </SortButtonWrapper>
        </Wrapper>
    );
};

export default AolFilterHeaderComponent;
